import React, { useState, useEffect } from "react";
import { MdClose, MdCheck } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import tw from "tailwind-styled-components";
import { Label, ScrollableContent } from "./Styles/InputStyles";
import { GoSearch } from "react-icons/go";

export default function SinglePropSelectModal({
    isOpen,
    onClose,
    onSelect,
    options = [],
    selectedProperty = null,
    isLoading = false,
}) {
    const [tempSelectedProperty, setTempSelectedProperty] = useState(selectedProperty);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        if (isOpen) {
            setTempSelectedProperty(selectedProperty);
            setSearchText("");
        }
    }, [isOpen, selectedProperty]);

    const handlePropertySelect = (property) => {
        setTempSelectedProperty(property);
        onSelect(property);
        onClose();
    };

    // Filter out properties where id == 0 and apply search filter
    const filteredOptions = options
        .filter(property => property.id !== 0)
        .filter(property =>
            property.name?.toLowerCase().includes(searchText.toLowerCase()) ||
            property.gId?.toLowerCase().includes(searchText.toLowerCase())
        );

    const clearSelection = () => {
        setTempSelectedProperty(null);
        onSelect(null);
        onClose();
    };

    const isSelected = (propertyId) => {
        return tempSelectedProperty?.id === propertyId;
    };

    if (!isOpen) return null;

    return (
        <Overlay>
            <ModalWrapper>
                <Header>
                    <h1 className="text-white text-lg font-medium">Select Property</h1>
                    <button onClick={onClose}>
                        <MdClose color="white" size={24} />
                    </button>
                </Header>

                {/* Search Box */}
                <div className="p-4 relative">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search properties..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                        />
                        {/* Search Icon */}
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <GoSearch className="w-4 h-4 text-gray-400" />
                        </div>
                    </div>
                </div>



                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <CircularProgress />
                        </div>
                    ) : filteredOptions.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {options.filter(property => property.id !== 0).length === 0
                                ? "No properties available"
                                : "No properties found"
                            }
                        </div>
                    ) : (
                        <div className="px-4 space-y-2">
                            {filteredOptions.map((property) => (
                                <OptionRow
                                    key={property.id}
                                    onClick={() => handlePropertySelect(property)}
                                    selected={isSelected(property.id)}
                                >
                                    <div className="flex flex-col flex-1">
                                        <Label className="font-medium text-sm">
                                            {property.name}
                                        </Label>
                                        <div className="text-xs text-gray-500 mt-1">
                                            ID: {property.gId || property.id}
                                        </div>
                                    </div>
                                    {isSelected(property.id) && (
                                        <MdCheck className="text-green-500" size={20} />
                                    )}
                                </OptionRow>
                            ))}
                        </div>
                    )}
                </ScrollableContent>
            </ModalWrapper>
        </Overlay>
    );
}

// Styled components
const Overlay = tw.div`fixed inset-0 z-50 flex items-center justify-end bg-black/50`;
const ModalWrapper = tw.div`bg-white w-11/12 h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const OptionRow = tw.div`
    flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50
    ${props => props.selected ? 'border-green-500 bg-green-50' : ''}
`;